import { useState, useCallback, useRef, useEffect } from 'react';
import type { Chart as ApiChart } from '../../api';
import {
  type ChartEditorState,
  type PaletteOption,
  buildEditorState,
  fieldMappingsToFieldConfig,
  DEFAULT_EDITOR_STATE,
} from '../../components/edit/visualConfig';

/**
 * Manages editor state, dirty tracking, and synchronization
 * from chart API responses (AI preview / initial load).
 *
 * Owns:
 * - `editorState` / `initialEditorState` state
 * - `isVisualDirty` derived flag
 * - `isPendingFieldUpdate` freeze flag
 * - `handleEditorStateChange` with synchronous freeze detection
 * - `syncEditorStateFromChart` for API response → state sync
 */
export function useEditorStateSync(
  chart: ApiChart | null,
  isNewChart: boolean,
  /** Dashboard-level theme. Used as the default palette when the chart has no persisted override. */
  dashboardTheme?: string,
) {
  // ── State ────────────────────────────────────────────────────────────────

  const [editorState, setEditorState] = useState<ChartEditorState>(() => {
    const themePalette = (dashboardTheme ?? 'default') as PaletteOption;
    if (isNewChart || !chart) {
      return {
        ...DEFAULT_EDITOR_STATE,
        chartStyle: { ...DEFAULT_EDITOR_STATE.chartStyle, palette: themePalette },
      };
    }
    const built = buildEditorState(
      chart.chartType ?? 'bar',
      chart.chartStyle as Record<string, unknown> | null,
      chart.chartFieldsConfig as Record<string, unknown> | null,
    );
    // If the chart has no persisted palette, inherit from dashboard theme
    const stored = chart.chartStyle as Record<string, unknown> | null;
    if (!stored || !('palette' in stored)) {
      built.chartStyle = { ...built.chartStyle, palette: themePalette };
    }
    return built;
  });

  const [initialEditorState, setInitialEditorState] = useState<ChartEditorState>(
    () => ({ ...editorState, chartStyle: { ...editorState.chartStyle }, chartFieldsConfig: { ...editorState.chartFieldsConfig } }),
  );
  const initialEditorStateRef = useRef<string>(JSON.stringify(editorState));
  const isVisualDirty = JSON.stringify(editorState) !== initialEditorStateRef.current;

  const [isPendingFieldUpdate, setIsPendingFieldUpdate] = useState(false);

  // ── Refs for debounced preview-update coordination ────────────────────

  const fieldsJsonRef = useRef<string>(JSON.stringify(editorState.fields ?? null));
  const sortLimitJsonRef = useRef<string>(JSON.stringify({
    sort: editorState.chartFieldsConfig.sort,
    limit: editorState.chartFieldsConfig.limit,
    limitOthers: editorState.chartFieldsConfig.limitOthers,
    sortField: editorState.chartFieldsConfig.sortField,
    sortDirection: editorState.chartFieldsConfig.sortDirection,
    breakdownSort: editorState.chartStyle.breakdownSort,
  }));

  // ── Change handler (synchronous pending flag) ─────────────────────────

  const handleEditorStateChange = useCallback((next: ChartEditorState) => {
    setEditorState((prev: ChartEditorState) => {
      const fieldsChanged =
        JSON.stringify(next.fields ?? null) !== JSON.stringify(prev.fields ?? null);
      const sortLimitChanged =
        next.chartFieldsConfig.sort !== prev.chartFieldsConfig.sort ||
        next.chartFieldsConfig.limit !== prev.chartFieldsConfig.limit ||
        next.chartFieldsConfig.limitOthers !== prev.chartFieldsConfig.limitOthers ||
        next.chartFieldsConfig.sortField !== prev.chartFieldsConfig.sortField ||
        next.chartFieldsConfig.sortDirection !== prev.chartFieldsConfig.sortDirection ||
        next.chartStyle.breakdownSort !== prev.chartStyle.breakdownSort;
      if (fieldsChanged || sortLimitChanged) {
        setIsPendingFieldUpdate(true);
      }
      return next;
    });
  }, []);

  // ── Sync from chart API response ──────────────────────────────────────

  const syncEditorStateFromChart = useCallback((c: ApiChart) => {
    const state = buildEditorState(
      c.chartType ?? 'bar',
      c.chartStyle as Record<string, unknown> | null,
      c.chartFieldsConfig as Record<string, unknown> | null,
    );
    if (!state.fields) {
      const fields = fieldMappingsToFieldConfig(
        c.fieldMappings as Record<string, unknown> | null,
        state.chartType,
      );
      if (fields) {
        state.fields = fields;
      }
    }
    // If the chart has no persisted palette override, inherit dashboard theme
    const stored = c.chartStyle as Record<string, unknown> | null;
    if (!stored || !('palette' in stored)) {
      state.chartStyle = { ...state.chartStyle, palette: (dashboardTheme ?? 'default') as PaletteOption };
    }
    setEditorState(state);
    setInitialEditorState({ ...state, chartStyle: { ...state.chartStyle }, chartFieldsConfig: { ...state.chartFieldsConfig } });
    initialEditorStateRef.current = JSON.stringify(state);
    fieldsJsonRef.current = JSON.stringify(state.fields ?? null);
    sortLimitJsonRef.current = JSON.stringify({
      sort: state.chartFieldsConfig.sort,
      limit: state.chartFieldsConfig.limit,
      limitOthers: state.chartFieldsConfig.limitOthers,
      sortField: state.chartFieldsConfig.sortField,
      sortDirection: state.chartFieldsConfig.sortDirection,
      breakdownSort: state.chartStyle.breakdownSort,
    });
  }, [dashboardTheme]);

  // ── Reset state when chart identity changes (e.g. navigation) ──────────

  const prevChartIdRef = useRef<string | undefined>(chart?.id);
  useEffect(() => {
    const currentId = chart?.id;
    if (currentId === prevChartIdRef.current) return;
    prevChartIdRef.current = currentId;

    // Skip reset if no chart (new chart mode)
    if (!chart) return;

    syncEditorStateFromChart(chart);
  }, [chart?.id, chart, syncEditorStateFromChart]);

  return {
    editorState,
    setEditorState,
    initialEditorState,
    initialEditorStateRef,
    isVisualDirty,
    isPendingFieldUpdate,
    setIsPendingFieldUpdate,
    handleEditorStateChange,
    syncEditorStateFromChart,
    fieldsJsonRef,
    sortLimitJsonRef,
  };
}